import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { removeFromArray } from '@reflact/tsutil';
import { AdminStageDef, ToolConfigTool } from '@shared/index';
import { firstValueFrom } from 'rxjs';
import { CounterService } from './counter.service';
import { BotidService } from './ragai/botid.service';
import { SocketAdminService } from './socketadmin.service';
import { ToastrService } from './toastr.service';

export type SkillItem = {
  id: string,
  name: string,
  type: ToolConfigTool
}

@Injectable({
  providedIn: 'root'
})
export class StageAdminService {
  public stages: AdminStageDef[] = [];

  constructor(
    public http: HttpClient,
    public adminSocket: SocketAdminService,
    private toastr: ToastrService,
    private counterService: CounterService,
    private botIdService: BotidService
  ) {
    this.initSockets();
  }

  public initSockets() {
    this.adminSocket.onStageCreated.subscribe((d) => {
      if (d.botId != this.botIdService.getBotId()) {
        return;
      }
      if (this.stages.some((s) => s._id === d._id)) {
        return;
      }
      this.stages.push(d);
    });
    this.adminSocket.onStageFieldUpdate.subscribe((d) => {
      if (d.botId != this.botIdService.getBotId()) {
        return;
      }
      const affectedStage = this.stages.find((s) => { return s._id == d.stageId; });
      if (affectedStage != null) {
        //@ts-expect-error
        affectedStage[d.key] = d.value;
      }
    });

    this.adminSocket.onStageDeleted.subscribe((d) => {
      if (d.botId != this.botIdService.getBotId()) {
        return;
      }
      removeFromArray(this.stages, ((s) => { return s._id == d.stageId; }));
    });

    this.adminSocket.onStageSkillCleanup.subscribe((d) => {
      if (d.botId != this.botIdService.getBotId()) {
        return;
      }
      for (const stage of this.stages) {
        stage.skills = stage.skills.filter((s) => s !== d.skillName);
      }
    });
  }

  public getStageNames() {
    const stageNames: string[] = [];
    this.stages.forEach(stage => stageNames.push(stage.name));
    return stageNames;
  }
  public getStageById(id: string) {
    return this.stages.find(stage => stage._id === id);
  }

  public async getSkillsForStage() {
    return await firstValueFrom(this.http.get<SkillItem[]>("/api/toolbox/bot/admin/stagemanager/skillsforstages/" + this.botIdService.getBotId()));
  }

  public async loadStages() {
    this.stages = await firstValueFrom(this.http.get<AdminStageDef[]>("/api/toolbox/bot/admin/stagemanager/listforbot/" + this.botIdService.getBotId()));
    this.counterService.setStageManager(this.botIdService.getBotId(), this.stages.length);
  }

  public async createStage(title: string) {
    this.counterService.addStageManager(this.botIdService.getBotId());
    const newStage = await firstValueFrom(this.http.post<AdminStageDef>("/api/toolbox/bot/admin/stagemanager/createStage/" + this.botIdService.getBotId(), { title }));
    if (!this.stages.some((s) => s._id === newStage._id)) {
      this.stages.push(newStage);
    }
    this.toastr.success("CREATED");
    return newStage;
  }

  public async updateStagesField(stageId: string, key: keyof AdminStageDef, value: AdminStageDef[keyof AdminStageDef]) {
    await firstValueFrom(this.http.post<AdminStageDef[]>("/api/toolbox/bot/admin/stagemanager/updatefield/" + this.botIdService.getBotId() + "/" + stageId,
      { key, value }
    ));
    if (key === "sortFloat") {
      this.stages.sort((a, b) => {
        if (a.sortFloat < b.sortFloat) {
          return -1;
        }
        if (a.sortFloat > b.sortFloat) {
          return 1;
        }
        return 0;
      });
    }
    this.toastr.success("SAVED");
  }

  public async deleteStage(stageId: string) {
    this.counterService.removeStageManager(this.botIdService.getBotId());
    await firstValueFrom(this.http.post<AdminStageDef[]>("/api/toolbox/bot/admin/stagemanager/deleteStage/" + this.botIdService.getBotId() + "/" + stageId, {}));
    this.toastr.success("DELETED");
  }

  public async duplicateStage(stageId: string) {
    this.counterService.addStageManager(this.botIdService.getBotId());
    await firstValueFrom(this.http.post<{ msg: string }>("/api/toolbox/bot/admin/stagemanager/duplicateStage/" + this.botIdService.getBotId() + "/" + stageId, {}));
    this.toastr.success("DUPLICATED");
  }
}